import * as bi from 'react-bootstrap-icons';
import { IClientVisaBrief } from '@bustickus/models-client';
import { MessagePoint } from '..';
import { ModalCalendar, SpoilerCard } from 'shared/components';
import { Button, Flex, Input, Message } from 'components';
import { useEffect, useState } from 'react';
import { isVisaExpirationDateValid, isVisaIssueDateValid, visaIsValid } from './helpers';
import { dateFormat } from 'common/helpers';
import { IVisaCard } from '../../types';

interface IProps {
  open: boolean;
  visa: IClientVisaBrief;
  onOpen: () => void;
  onDeleteClick: () => void;
  onChange: (data: IVisaCard) => void;
  initial?: IVisaCard | null;
}

function VisaCard(props: IProps) {
  const [visaNumber, setVisaNumber] = useState<string>(props.initial?.input?.number ?? '');
  const [dayCount, setDayCount] = useState<string>(props.initial?.input?.stayDaysCount.toString() ?? '');
  const [issueDate, setIssueDate] = useState<Date | null>(
    props.initial?.input != null ? new Date(props.initial.input.issueDate) : null
  );
  const [expirationDate, setExpirationDate] = useState<Date | null>(
    props.initial?.input != null ? new Date(props.initial.input.expirationDate) : null
  );

  const [issueDateModal, setIssueDateModal] = useState<boolean>(false);
  const [expirationDateModal, setExpirationDateModal] = useState<boolean>(false);

  const [visaNumberTouched, setVisaNumberTouched] = useState<boolean>(false);
  const [dayCountTouched, setDayCountTouched] = useState<boolean>(false);
  const [issueDateTouched, setIssueDateTouched] = useState<boolean>(false);
  const [expirationDateTouched, setExpirationDateTouched] = useState<boolean>(false);

  useEffect(() => {
    handleChange(visaNumber, dayCount, issueDate, expirationDate);
  }, []);

  const handleVisaNumberChange = (value: string) => {
    if (!visaNumberTouched) {
      setVisaNumberTouched(true);
    }

    setVisaNumber(value);
    handleChange(value, dayCount, issueDate, expirationDate);
  };

  const handleDayCountChange = (value: string) => {
    if (!dayCountTouched) {
      setDayCountTouched(true);
    }

    setDayCount(value);
    handleChange(visaNumber, value, issueDate, expirationDate);
  };

  const handleIssueDateChange = (issueDate: Date) => {
    if (!issueDateTouched) {
      setIssueDateTouched(true);
    }

    if (issueDateModal) {
      setIssueDateModal(false);
    }

    setIssueDate(issueDate);
    handleChange(visaNumber, dayCount, issueDate, expirationDate);
  };

  const handleExpireDateChange = (expireDate: Date) => {
    if (!expirationDateTouched) {
      setExpirationDateTouched(true);
    }

    if (expirationDateModal) {
      setExpirationDateModal(false);
    }

    setExpirationDate(expireDate);
    handleChange(visaNumber, dayCount, issueDate, expireDate);
  };

  const handleChange = (number: string, dayCount: string, issueDate: Date | null, expireDate: Date | null) => {
    const days = Number(dayCount);

    if (
      issueDate != null &&
      expireDate != null &&
      isVisaIssueDateValid(issueDate) &&
      isVisaExpirationDateValid(issueDate, expireDate) &&
      Number.isSafeInteger(days) &&
      days > 0 &&
      visaIsValid(number)
    ) {
      props.onChange({
        visa: props.visa,
        input: {
          visa: props.visa,
          number: number,
          issueDate: issueDate,
          expirationDate: expireDate,
          stayDaysCount: days,
        },
      });
    } else {
      props.onChange({ visa: props.visa, input: null });
    }
  };

  const isVisaNumberValid = visaIsValid(visaNumber);
  const isIssueDateValid = isVisaIssueDateValid(issueDate);
  const isExpirationDateValid = isVisaExpirationDateValid(issueDate, expirationDate);
  const isStayDayCountValid = Number.isSafeInteger(Number(dayCount)) && Number(dayCount) > 0;
  const isInputError = !isIssueDateValid || !isExpirationDateValid || !isVisaNumberValid || !isStayDayCountValid;

  return (
    <SpoilerCard
      open={props.open}
      onOpenClick={props.onOpen}
      title={`Виза (${props.visa.name})`}
    >
      {issueDateModal && (
        <ModalCalendar
          onClose={() => setIssueDateModal(false)}
          onSelect={handleIssueDateChange}
          date={issueDate ?? new Date(2025, 0, 1)}
        />
      )}

      {expirationDateModal && (
        <ModalCalendar
          onClose={() => setExpirationDateModal(false)}
          onSelect={handleExpireDateChange}
          date={expirationDate ?? new Date(2025, 0, 1)}
        />
      )}

      <Flex
        direction='column'
        gap={10}
      >
        <Flex
          direction='column'
          gap={5}
        >
          <Input
            type='text'
            label='Номер визы'
            placeholder='A0000000'
            icon={<bi.Airplane />}
            value={visaNumber}
            onChange={handleVisaNumberChange}
            error={visaNumberTouched && !isVisaNumberValid}
          />

          <Flex
            direction='row'
            gap={10}
          >
            <Input
              readonly
              type='text'
              label='Въехать с'
              error={issueDateTouched && !isIssueDateValid}
              value={issueDate != null ? dateFormat(issueDate, 'DD MMMM YYYY').datetime : ''}
              onClick={() => setIssueDateModal(true)}
              placeholder='Нажмите для выбора...'
            />
            <Input
              readonly
              type='text'
              label='Въехать до'
              error={expirationDateTouched && !isExpirationDateValid}
              value={expirationDate != null ? dateFormat(expirationDate, 'DD MMMM YYYY').datetime : ''}
              onClick={() => setExpirationDateModal(true)}
              placeholder='Нажмите для выбора...'
            />
          </Flex>

          <Input
            type='number'
            label='Максимальное количество дней пребывания'
            placeholder='90'
            icon={<bi.Clock />}
            error={dayCountTouched && !isStayDayCountValid}
            value={dayCount}
            onChange={handleDayCountChange}
          />
        </Flex>

        {isInputError && (
          <Message type='error'>
            <Flex
              direction='column'
              gap={5}
            >
              {!isVisaNumberValid && <MessagePoint type='error'>Номер визы указан неверно!</MessagePoint>}
              {!isIssueDateValid && (
                <MessagePoint type='error'>Дата начала действия визы указана неверно!</MessagePoint>
              )}
              {!isIssueDateValid && (
                <MessagePoint type='error'>
                  Дата окончания действия визы указана неверно или виза просрочена!
                </MessagePoint>
              )}
              {!isStayDayCountValid && (
                <MessagePoint type='error'>Количество дней действия указано неверено!</MessagePoint>
              )}
            </Flex>
          </Message>
        )}
        {!isInputError && (
          <Message type='success'>
            <MessagePoint type='success'>
              <b>Отлично!</b> Данные визы выглядят корректно!
            </MessagePoint>
          </Message>
        )}
        <Button
          color='transparent-red'
          onClick={props.onDeleteClick}
        >
          <Flex
            gap={5}
            alignItems='center'
            justifyContent='center'
          >
            <bi.Trash />
            <span>Удалить визу</span>
          </Flex>
        </Button>
      </Flex>
    </SpoilerCard>
  );
}

export default VisaCard;
